import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  User, MessageCircle, Crown, Settings, ChevronRight, 
  Shield, Loader2, AlertCircle, Star, Check, Mail, Eye, EyeOff, Lock, X
} from 'lucide-react';
import { API, BRAND } from '../config';
import { useAuth } from '../contexts/AuthContext';
import LegalFooter from '../components/LegalFooter';
import { PasswordStrengthIndicator, isPasswordValid } from '../components/PasswordStrength';
import { getPushTokenIfGranted as requestNotificationPermission, areNotificationsEnabled, isNotificationSupported, getPushPlatform } from '../services/pushService';
import { isIOS } from '../utils/platform';
// iter246 — Meta Pixel helpers for CAPI dedup + fbc/fbp capture
import { getFbAttributionPayload, trackCompleteRegistration, setMetaConsent, ensurePixelInit } from '../lib/metaPixel';
import { MetaConsentReminder, EmailConsentReminder, metaReminderSeen, markMetaReminderSeen, emailReminderSeen, markEmailReminderSeen } from '../components/MetaConsentReminder';
import { getSignupAttribution } from '../lib/utmCapture';
import SignupTermsCheckbox from '../components/consent/SignupTermsCheckbox';
import TurnstileWidget from '../components/TurnstileWidget';
import { getStoredInviteCode, inviteCodeSource } from '../lib/inviteCode';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, user, setUser, checkAuth } = useAuth();
  
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab');
  
  const [isLogin, setIsLogin] = useState(tabParam !== 'register');
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [isOver18, setIsOver18] = useState(false);
  const [birthDate, setBirthDate] = useState("");
  const [phone, setPhone] = useState("");
  // iter245 — Codice amico opzionale al signup. Il backend lo accetta sia
  // nel register classico (campo `referral_code` su UserCreate) sia nei
  // flussi OAuth tramite `consent_data.referral_code`.
  const [referralCode, setReferralCode] = useState(getStoredInviteCode);
  const [acceptsNotifications, setAcceptsNotifications] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  // iter268 — Consent dedicato Meta Pixel/CAPI, pre-spuntato (dark pattern
  // legale: visibile, deselezionabile, revocabile dal profilo). Separato
  // da `marketingConsent` (email promozionali) perché l'utente lascia
  // sempre quello OFF ma il tracking Meta serve per attribuzione campagne.
  const [metaTrackingConsent, setMetaTrackingConsent] = useState(false);
  const [acceptsTerms, setAcceptsTerms] = useState(false);
  // iter254c — Collapse optional fields (phone, referral, marketing opt-in)
  // behind a toggle to reduce perceived friction on the signup form.
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  useEffect(() => {
    const savedEmail = localStorage.getItem('remembered_email');
    const savedPassword = localStorage.getItem('remembered_password');
    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);
  
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [captcha, setCaptcha] = useState({ register: "", forgot: "", resend: "" });
  const [captchaReset, setCaptchaReset] = useState({ register: 0, forgot: 0, resend: 0 });
  const setCaptchaFor = (k) => (t) => setCaptcha((c) => ({ ...c, [k]: t }));
  const resetCaptcha = (k) => setCaptchaReset((r) => ({ ...r, [k]: r[k] + 1 }));  // tokens are single-use
  const CAPTCHA_MSG = "Completa la verifica anti-bot qui sopra";
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState("");
  
  // Apple Sign In state
  const [showAppleConsent, setShowAppleConsent] = useState(false);
  const [appleAuthData, setAppleAuthData] = useState(null);
  const [appleConsent18, setAppleConsent18] = useState(false);
  const [appleConsentNotif, setAppleConsentNotif] = useState(false);
  const [appleConsentMarketing, setAppleConsentMarketing] = useState(false);
  const [appleConsentMeta, setAppleConsentMeta] = useState(false);
  const [appleConsentTerms, setAppleConsentTerms] = useState(false);
  const [appleConsentReferralCode, setAppleConsentReferralCode] = useState(getStoredInviteCode);

  // Google GIS consent state (for new users)
  const [showGoogleConsent, setShowGoogleConsent] = useState(false);
  const [googlePendingData, setGooglePendingData] = useState(null);
  const [googleConsent18, setGoogleConsent18] = useState(false);
  const [googleConsentNotif, setGoogleConsentNotif] = useState(false);
  const [googleConsentMarketing, setGoogleConsentMarketing] = useState(false);
  const [googleConsentMeta, setGoogleConsentMeta] = useState(false);
  // iter272 — one-time optional reminder for the Meta consent
  const [metaReminder, setMetaReminder] = useState(null);
  // Ref guards against the double submit (native submit + requestSubmit).
  const metaReminderOpenRef = useRef(false);
  const closeMetaReminder = () => { metaReminderOpenRef.current = false; setMetaReminder(null); };
  const askMetaOnce = (consent, retry, setConsent) => {
    if (consent) return false;
    if (metaReminderOpenRef.current) return true;
    if (metaReminderSeen()) return false;
    markMetaReminderSeen();
    metaReminderOpenRef.current = true;
    setMetaReminder({
      accept: () => { closeMetaReminder(); setConsent(true); ensurePixelInit(); retry(true); },
      decline: () => { closeMetaReminder(); retry(false); },
    });
    return true;
  };
  // One-time optional reminder for the email-marketing consent (shown after the Meta one).
  const [emailReminder, setEmailReminder] = useState(null);
  const emailReminderOpenRef = useRef(false);
  const closeEmailReminder = () => { emailReminderOpenRef.current = false; setEmailReminder(null); };
  const askEmailOnce = (consent, retry, setConsent) => {
    if (consent) return false;
    if (emailReminderOpenRef.current) return true;
    if (emailReminderSeen()) return false;
    markEmailReminderSeen();
    emailReminderOpenRef.current = true;
    setEmailReminder({
      accept: () => { closeEmailReminder(); setConsent(true); retry(true); },
      decline: () => { closeEmailReminder(); retry(false); },
    });
    return true;
  };
  const [googleConsentTerms, setGoogleConsentTerms] = useState(false);
  const [googleConsentReferralCode, setGoogleConsentReferralCode] = useState(getStoredInviteCode);
  // One Tap callback ref (avoids stale closure in GIS initialize)
  const oneTapCallbackRef = useRef(null);

  // iter279 — Social login from the "Accedi" tab by someone without an account:
  // switch to "Registrati" and keep the social data (5 min) so the next tap on
  // "Registrati con Google/Apple" opens the consent step without re-auth.
  const isLoginRef = useRef(isLogin);
  isLoginRef.current = isLogin;
  const pendingSocialRef = useRef(null); // { kind, data, at }
  const PENDING_SOCIAL_MS = 4 * 60 * 1000;
  const sendToRegister = (kind, data) => {
    pendingSocialRef.current = { kind, data, at: Date.now() };
    setIsLogin(false);
    setError('');
    setSuccess('Non hai ancora un account RVINCENTE: completa la registrazione qui sotto.');
  };
  const takePendingSocial = (kind) => {
    const p = pendingSocialRef.current;
    if (!p || p.kind !== kind || Date.now() - p.at > PENDING_SOCIAL_MS) return null;
    pendingSocialRef.current = null;
    return p.data;
  };

  const handleOneTapResponse = async (response) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/google/one-tap`, {
        credential: response.credential
      });
      if (res.data.requires_consent) {
        const pending = {
          pending_token: res.data.pending_token,
          email: res.data.email,
          name: res.data.name,
          picture: res.data.picture
        };
        setLoading(false);
        if (isLoginRef.current) { sendToRegister('google', pending); return; }
        setGooglePendingData(pending);
        setShowGoogleConsent(true);
        return;
      }
      if (res.data.access_token) {
        localStorage.setItem('auth_token', res.data.access_token);
        localStorage.removeItem('logged_out');
        axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
      }
      setUser(res.data.user);
      try {
        const fcmToken = await requestNotificationPermission();
        if (fcmToken) await axios.post(`${API}/notifications/register-token`, { token: fcmToken, platform: getPushPlatform() });
      } catch (e) { console.warn('Push registration failed:', e.message); }
      navigate('/dashboard');
    } catch (err) {
      setError(typeof (err.response?.data?.detail) === 'string' ? err.response.data.detail : "Errore durante l'autenticazione Google");
    }
    setLoading(false);
  };
  oneTapCallbackRef.current = handleOneTapResponse;

  // Initialize One Tap prompt (GIS script loaded via index.html)
  useEffect(() => {
    if (user) return;
    const initOneTap = () => {
      if (!window.google?.accounts?.id) return false;
      try {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: (resp) => oneTapCallbackRef.current?.(resp),
          auto_select: true,
          cancel_on_tap_outside: true,
        });
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed()) {
            console.log('One Tap not displayed:', notification.getNotDisplayedReason());
          }
        });
        return true;
      } catch (e) {
        console.warn('One Tap init error:', e.message);
        return false;
      }
    };
    // Try immediately, then retry every 500ms until GIS loads (max 10s)
    if (!initOneTap()) {
      let retries = 0;
      const interval = setInterval(() => {
        retries++;
        if (initOneTap() || retries >= 20) clearInterval(interval);
      }, 500);
      return () => {
        clearInterval(interval);
        if (window.google?.accounts?.id) window.google.accounts.id.cancel();
      };
    }
    return () => {
      if (window.google?.accounts?.id) window.google.accounts.id.cancel();
    };
  }, [user]);

  const handleAppleSignIn = async () => {
    setLoading(true);
    setError('');
    if (!isLogin && metaTrackingConsent) setAppleConsentMeta(true);
    if (!isLogin && marketingConsent) setAppleConsentMarketing(true);
    const pendingApple = !isLogin && takePendingSocial('apple');
    if (pendingApple) {
      setAppleAuthData(pendingApple);
      setShowAppleConsent(true);
      setLoading(false);
      return;
    }
    try {
      const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
      const result = await SignInWithApple.authorize({
        clientId: 'it.rvincente.app',
        redirectURI: 'https://app.rvincente.it',
        scopes: 'email name',
        state: 'apple_login',
      });
      
      const credential = result.response;
      const appleData = {
        identityToken: credential.identityToken,
        email: credential.email,
        givenName: credential.givenName,
        familyName: credential.familyName,
        bundleId: 'it.rvincente.app',
      };

      let res = await axios.post(`${API}/auth/apple`, appleData);
      
      if (res.data.requires_consent) {
        setLoading(false);
        if (isLogin) { sendToRegister('apple', appleData); return; }
        // New user - show consent modal
        setAppleAuthData(appleData);
        setShowAppleConsent(true);
        setLoading(false);
        return;
      }

      if (res.data.access_token) {
        localStorage.setItem('token', res.data.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
        try {
          const fcmToken = await requestNotificationPermission();
          if (fcmToken) await axios.post(`${API}/notifications/register-token`, { token: fcmToken, platform: getPushPlatform() });
        } catch (e) {}
        window.location.href = '/dashboard';
      }
    } catch (e) {
      if (e.message?.includes('canceled') || e.code === '1001') {
        // User cancelled
      } else {
        console.error('Apple Sign In error:', e);
        setError('Errore durante l\'accesso con Apple');
      }
    }
    setLoading(false);
  };

  const handleAppleConsentSubmit = async (metaOverride, emailOverride) => {
    if (!appleConsentTerms) { setError('Devi confermare di avere almeno 18 anni e accettare i Termini di servizio'); return; }
    const metaOk = typeof metaOverride === 'boolean' ? metaOverride : appleConsentMeta;
    if (typeof metaOverride !== 'boolean' && askMetaOnce(metaOk, (m) => handleAppleConsentSubmit(m), setAppleConsentMeta)) return;
    const emailOk = typeof emailOverride === 'boolean' ? emailOverride : appleConsentMarketing;
    if (typeof emailOverride !== 'boolean' && askEmailOnce(emailOk, (em) => handleAppleConsentSubmit(metaOk, em), setAppleConsentMarketing)) return;
    setLoading(true);
    setError('');
    try {
      const data = { ...appleAuthData, consent_data: { is_over_18: appleConsentTerms, accepts_notifications: true, marketing_consent: emailOk, meta_tracking_consent: metaOk, accepts_terms: appleConsentTerms, referral_code: (appleConsentReferralCode || "").trim().toUpperCase() || undefined, referral_code_source: inviteCodeSource(appleConsentReferralCode), ...getSignupAttribution() }, ...getFbAttributionPayload(), ...getSignupAttribution() };
      const res = await axios.post(`${API}/auth/apple`, data);
      if (res.data.access_token) {
        localStorage.setItem('token', res.data.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
        // iter246 — fire Pixel for Apple signups (new user branch)
        // iter250 — GATED on marketing_consent (GDPR + Meta dedup):
        // if the user didn't consent we must NOT fire client-side either,
        // otherwise Pixel+CAPI counts drift (Meta Diagnostica flags "server
        // sent fewer events than Pixel"). Backend already gates CAPI the
        // same way in auth.py.
        if (res.data?.status === 'new') setMetaConsent(metaOk);
        if (res.data?.status === 'new' && res.data?.user?.user_id && metaOk) {
          trackCompleteRegistration(res.data.user.user_id);
        }
        try {
          const fcmToken = await requestNotificationPermission();
          if (fcmToken) await axios.post(`${API}/notifications/register-token`, { token: fcmToken, platform: getPushPlatform() });
        } catch (e) {}
        setShowAppleConsent(false);
        window.location.href = '/dashboard';
      }
    } catch (err) {
      setError(typeof (err.response?.data?.detail) === 'string' ? err.response.data.detail : 'Errore durante la registrazione');
    }
    setLoading(false);
  };
  const [forgotError, setForgotError] = useState("");

  // Google login polling state
  const [googlePolling, setGooglePolling] = useState(false);
  const [loginIntentId, setLoginIntentId] = useState(null);
  const googleModeRef = useRef('register');

  // Google login — opens Chrome, TWA stays and polls
  const handleGoogleLogin = () => {
    setError('');
    if (!isLogin && metaTrackingConsent) setGoogleConsentMeta(true);
    if (!isLogin && marketingConsent) setGoogleConsentMarketing(true);
    const pendingGoogle = !isLogin && takePendingSocial('google');
    if (pendingGoogle) {
      setGooglePendingData(pendingGoogle);
      setShowGoogleConsent(true);
      return;
    }
    googleModeRef.current = isLogin ? 'login' : 'register';
    const intentId = crypto.randomUUID();
    setLoginIntentId(intentId);
    setGooglePolling(true);

    // Create intent record on backend
    axios.post(`${API}/auth/google/create-intent`, { intent_id: intentId }).catch(() => {});

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = window.location.origin + '/auth/google/callback';
    const scope = 'openid email profile';
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&prompt=select_account&state=${intentId}`;
    window.open(authUrl, '_blank');
  };

  // Polling: TWA checks every 2s if login completed in Chrome
  useEffect(() => {
    if (!googlePolling || !loginIntentId) return;
    let stopped = false;

    const poll = setInterval(async () => {
      if (stopped) return;
      try {
        const res = await axios.get(`${API}/auth/google/poll?intent_id=${loginIntentId}`);
        if (res.data.status === 'completed' && res.data.token) {
          stopped = true;
          clearInterval(poll);
          setGooglePolling(false);
          localStorage.setItem('auth_token', res.data.token);
          localStorage.removeItem('logged_out');
          axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
          setUser(res.data.user);
          try {
            const fcmToken = await requestNotificationPermission();
            if (fcmToken) await axios.post(`${API}/notifications/register-token`, { token: fcmToken, platform: getPushPlatform() });
          } catch (e) {}
          navigate('/dashboard');
        } else if (res.data.status === 'requires_consent') {
          stopped = true;
          clearInterval(poll);
          setGooglePolling(false);
          const pending = {
            pending_token: res.data.pending_token,
            email: res.data.email,
            name: res.data.name,
            picture: res.data.picture
          };
          if (googleModeRef.current === 'login') { sendToRegister('google', pending); return; }
          setGooglePendingData(pending);
          setShowGoogleConsent(true);
        } else if (res.data.status === 'failed') {
          stopped = true;
          clearInterval(poll);
          setGooglePolling(false);
          setError("Errore durante l'accesso con Google. Riprova.");
        }
      } catch (e) { /* polling error, retry */ }
    }, 2000);

    // Timeout after 5 minutes
    const timeout = setTimeout(() => {
      if (!stopped) {
        clearInterval(poll);
        setGooglePolling(false);
        setError('Login scaduto. Riprova.');
      }
    }, 5 * 60 * 1000);

    return () => { stopped = true; clearInterval(poll); clearTimeout(timeout); };
  }, [googlePolling, loginIntentId, navigate, setUser]);

  const handleGoogleConsentSubmit = async (metaOverride, emailOverride) => {
    if (!googleConsentTerms) { setError('Devi confermare di avere almeno 18 anni e accettare i Termini di servizio'); return; }
    const metaOk = typeof metaOverride === 'boolean' ? metaOverride : googleConsentMeta;
    if (typeof metaOverride !== 'boolean' && askMetaOnce(metaOk, (m) => handleGoogleConsentSubmit(m), setGoogleConsentMeta)) return;
    const emailOk = typeof emailOverride === 'boolean' ? emailOverride : googleConsentMarketing;
    if (typeof emailOverride !== 'boolean' && askEmailOnce(emailOk, (em) => handleGoogleConsentSubmit(metaOk, em), setGoogleConsentMarketing)) return;
    setLoading(true);
    setError('');
    try {
      const consentData = { is_over_18: googleConsentTerms, accepts_notifications: true, marketing_consent: emailOk, meta_tracking_consent: metaOk, accepts_terms: googleConsentTerms, referral_code: (googleConsentReferralCode || "").trim().toUpperCase() || undefined, referral_code_source: inviteCodeSource(googleConsentReferralCode), ...getSignupAttribution() };
      const res = await axios.post(`${API}/auth/google/exchange`, {
        pending_token: googlePendingData.pending_token,
        consent_data: consentData,
        // iter246 — Meta Pixel attribution
        ...getFbAttributionPayload(),
        // iter251 — UTM also at top level as a fallback (older backend code
        // reads from body before falling back to consent_data).
        ...getSignupAttribution(),
      });
      if (res.data.access_token) {
        localStorage.setItem('auth_token', res.data.access_token);
        localStorage.removeItem('logged_out');
        axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
      }
      setUser(res.data.user);
      // iter246 — fire Pixel event with CAPI-matching event_id when this
      // was a brand new signup (backend returns `status: 'new'`).
      // iter250 — gated on marketing_consent to stay aligned with CAPI
      // (both sides must gate identically or Meta drops dedup).
      if (res.data?.status === 'new') setMetaConsent(metaOk);
      if (res.data?.status === 'new' && res.data?.user?.user_id && metaOk) {
        trackCompleteRegistration(res.data.user.user_id);
      }
      try {
        const fcmToken = await requestNotificationPermission();
        if (fcmToken) await axios.post(`${API}/notifications/register-token`, { token: fcmToken, platform: getPushPlatform() });
      } catch (e) { console.warn('Push registration failed:', e.message); }
      setShowGoogleConsent(false);
      navigate('/dashboard');
    } catch (err) {
      setError(typeof (err.response?.data?.detail) === 'string' ? err.response.data.detail : 'Errore durante la registrazione');
    }
    setLoading(false);
  };
  
  const [showForceChangePassword, setShowForceChangePassword] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [changeLoading, setChangeLoading] = useState(false);
  const [changeError, setChangeError] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState("");
  const [resendError, setResendError] = useState("");
  
  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  
  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [twoFaToken, setTwoFaToken] = useState("");
  const [twoFaMethod, setTwoFaMethod] = useState("email"); // email | totp
  const [twoFaCode, setTwoFaCode] = useState("");
  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [twoFaError, setTwoFaError] = useState("");

  const handleVerify2FA = async () => {
    const trimmed = twoFaCode.trim();
    // Accept 6-digit TOTP/email OR 8-char backup code when method=totp
    const is6 = /^\d{6}$/.test(trimmed);
    const is8 = twoFaMethod === 'totp' && /^[A-Za-z0-9]{8}$/.test(trimmed);
    if (!is6 && !is8) {
      setTwoFaError(twoFaMethod === 'totp'
        ? "Inserisci un codice a 6 cifre (app) o a 8 caratteri (backup)"
        : "Inserisci il codice a 6 cifre");
      return;
    }
    setTwoFaLoading(true);
    setTwoFaError("");
    try {
      const response = await axios.post(`${API}/auth/verify-2fa`, {
        two_fa_token: twoFaToken,
        code: twoFaCode
      });
      
      if (response.data.access_token) {
        localStorage.setItem('auth_token', response.data.access_token);
        localStorage.setItem('token', response.data.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
        
        if (rememberMe) {
          localStorage.setItem('remembered_email', email);
          localStorage.setItem('remembered_password', password);
        } else {
          localStorage.removeItem('remembered_email');
          localStorage.removeItem('remembered_password');
        }
        
        try {
          const fcmToken = await requestNotificationPermission();
          if (fcmToken) await axios.post(`${API}/notifications/register-token`, { token: fcmToken, platform: getPushPlatform() });
        } catch (e) {}
        
        window.location.href = '/dashboard';
      }
    } catch (err) {
      setTwoFaError(err.response?.data?.detail || "Errore nella verifica del codice");
    }
    setTwoFaLoading(false);
  };

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const handleSubmit = async (e, metaOverride, emailOverride) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");
    
    try {
      if (isLogin) {
        const response = await axios.post(`${API}/auth/login`, { email, password, remember_me: rememberMe });
        
        if (response.data.requires_2fa) {
          setTwoFaToken(response.data.two_fa_token);
          setTwoFaMethod(response.data.method || "email");
          setShow2FA(true);
          setLoading(false);
          return;
        }
        
        if (response.data.requires_password_change) {
          setTempToken(response.data.access_token);
          setShowForceChangePassword(true);
          setLoading(false);
          return;
        }
        
        await login(email, password, rememberMe);
        
        if (rememberMe) {
          localStorage.setItem('remembered_email', email);
          localStorage.setItem('remembered_password', password);
        } else {
          localStorage.removeItem('remembered_email');
          localStorage.removeItem('remembered_password');
        }
        
        const pendingToken = localStorage.getItem('pending_fcm_token');
        if (pendingToken) {
          try {
            await axios.post(`${API}/notifications/register-token`, { token: pendingToken, platform: getPushPlatform() });
            localStorage.removeItem('pending_fcm_token');
          } catch (tokenError) {
            console.error('Failed to register FCM token:', tokenError);
          }
        }
        
        if (!pendingToken && areNotificationsEnabled()) {
          try {
            const freshToken = await requestNotificationPermission();
            if (freshToken) {
              await axios.post(`${API}/notifications/register-token`, { token: freshToken, platform: getPushPlatform() });
            }
          } catch (tokenError) {
            console.error('Failed to get/register fresh FCM token:', tokenError);
          }
        }
        
        navigate("/dashboard");
      } else {
        if (!isPasswordValid(password)) {
          setError("La password non soddisfa i requisiti di sicurezza");
          setLoading(false);
          return;
        }

        if (!acceptsTerms) {
          setError("Devi confermare di avere almeno 18 anni e accettare i Termini di servizio");
          setLoading(false);
          return;
        }
        const metaOk = typeof metaOverride === 'boolean' ? metaOverride : metaTrackingConsent;
        if (typeof metaOverride !== 'boolean' && askMetaOnce(metaOk, (m) => handleSubmit(null, m), setMetaTrackingConsent)) {
          setLoading(false);
          return;
        }
        const emailOk = typeof emailOverride === 'boolean' ? emailOverride : marketingConsent;
        if (typeof emailOverride !== 'boolean' && askEmailOnce(emailOk, (em) => handleSubmit(null, metaOk, em), setMarketingConsent)) {
          setLoading(false);
          return;
        }

        // IMPORTANT: do NOT trigger the OS notification prompt here. Asking
        // for the OS permission on the registration form yields ~30-40%
        // acceptance because the user hasn't seen any value yet. We now
        // defer this to a custom in-app "soft ask" modal shown after the
        // first value moment (match click / VIP tab). See
        // hooks/useNotificationSoftAsk.js and components/NotificationSoftAsk.jsx.

        if (!captcha.register) {
          setError(CAPTCHA_MSG);
          setLoading(false);
          return;
        }
        const response = await axios.post(`${API}/auth/register`, {
          email, password, name,
          turnstile_token: captcha.register,
          // iter254e — Max-simple signup. The legal consents are absorbed
          // into the submit button legend ("Registrandoti dichiari di
          // essere maggiorenne e accetti T&C/Privacy"). birth_date,
          // marketing, phone and referral are now asked post-signup via a
          // non-blocking profile-completion nudge.
          is_over_18: acceptsTerms,
          accepts_notifications: true,
          marketing_consent: emailOk,
          // iter268 — Consent dedicato Meta Pixel/CAPI (checkbox pre-spuntata).
          // Il backend lo usa come `limited_data_use=not meta_tracking_consent`
          // nel CAPI → Meta applica LDU se false (modalità GDPR-safe) oppure
          // full-data-use se true (= attribuzione + custom audiences).
          meta_tracking_consent: metaOk,
          accepts_terms: acceptsTerms,
          phone: phone || undefined,
          referral_code: (referralCode || "").trim().toUpperCase() || undefined,
          referral_code_source: inviteCodeSource(referralCode),
          // iter246 — Meta Pixel attribution: forward fbp/fbc cookies so the
          // CAPI event sent by the backend can be deduplicated against the
          // Pixel event we fire client-side right below.
          ...getFbAttributionPayload(),
          // iter251 — internal UTM attribution captured on landing. Saved
          // to the user doc so the admin "Acquisizione per campagna"
          // widget can group signups by Meta campaign/creative.
          ...getSignupAttribution(),
        });

        // iter268 — Persist Meta consent in localStorage per il Pixel JS
        // (gated on consent). Fire CompleteRegistration solo se consent dato.
        setMetaConsent(metaOk);
        if (response.data?.user_id && metaOk) {
          trackCompleteRegistration(response.data.user_id);
        }
        if (response.data?.access_token) {
          // Reuse the AuthContext login helper so the token + user are
          // persisted the same way as a normal login.
          try {
            await login(email, password, false);
          } catch {
            // Fallback: cookie is already set by the backend, hard reload
            // to pick up the session.
          }
          navigate("/dashboard");
        } else {
          // Fallback to the legacy "verify email" screen if the backend
          // (old version) didn't return a token.
          setRegisteredEmail(email);
          setShowRegistrationSuccess(true);
          setEmail(""); setPassword(""); setConfirmPassword(""); setName("");
          setBirthDate(""); setPhone(""); setReferralCode(""); setIsOver18(false); setAcceptsNotifications(false); setMarketingConsent(false); setAcceptsTerms(false);
        }
      }
    } catch (err) {
      if (!isLoginRef.current) resetCaptcha('register');
      if (err.response?.status === 429 && typeof err.response?.data?.detail !== 'string') {
        setError("Troppi tentativi di accesso. Attendi 15 minuti e riprova.");
      } else {
        setError(typeof (err.response?.data?.detail) === 'string' ? err.response.data.detail : "Errore durante l'autenticazione");
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError(""); setForgotSuccess("");
    if (!captcha.forgot) { setForgotError(CAPTCHA_MSG); return; }
    setForgotLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail, turnstile_token: captcha.forgot });
      setForgotSuccess(response.data.message);
      setForgotEmail("");
    } catch (err) {
      setForgotError(err.response?.data?.detail || "Errore durante il recupero password");
    } finally { setForgotLoading(false); resetCaptcha('forgot'); }
  };
  
  const handleResendVerification = async (e) => {
    e.preventDefault();
    setResendError(""); setResendSuccess("");
    if (!captcha.resend) { setResendError(CAPTCHA_MSG); return; }
    setResendLoading(true);
    try {
      const response = await axios.post(`${API}/auth/resend-verification-email`, { email: resendEmail, turnstile_token: captcha.resend });
      setResendSuccess(response.data.message);
      setResendEmail("");
    } catch (err) {
      setResendError(err.response?.data?.detail || "Errore durante l'invio dell'email");
    } finally { setResendLoading(false); resetCaptcha('resend'); }
  };
  
  const handleForceChangePassword = async (e) => {
    e.preventDefault(); setChangeError("");
    if (newPassword !== confirmNewPassword) { setChangeError("Le password non coincidono"); return; }
    if (!isPasswordValid(newPassword)) { setChangeError("La password non soddisfa i requisiti di sicurezza"); return; }
    setChangeLoading(true);
    try {
      await axios.post(`${API}/auth/force-change-password`, 
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );
      setShowForceChangePassword(false); setTempToken(""); setNewPassword(""); setConfirmNewPassword("");
      setSuccess("Password aggiornata! Effettua il login con la nuova password.");
      setPassword("");
    } catch (err) {
      setChangeError(err.response?.data?.detail || "Errore durante il cambio password");
    } finally { setChangeLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        {/* 2FA Verification Screen */}
        {show2FA && (
          <div data-testid="2fa-screen">
            <div className="text-center mb-10">
              <img src={BRAND.logo} alt={BRAND.name} className="w-24 h-24 mx-auto mb-4 rounded-2xl object-contain shadow-[0_0_30px_rgba(245,166,35,0.2)]" />
              <h1 className="text-5xl font-black uppercase tracking-tighter text-white mb-1" style={{fontFamily: 'Barlow Condensed'}}>{BRAND.name}</h1>
            </div>
            <div className="glass rounded-3xl p-6">
              <div className="text-center mb-6">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#F5A623]/10 flex items-center justify-center">
                  <Shield className="w-7 h-7 text-[#F5A623]" />
                </div>
                <h2 className="text-lg font-bold text-white mb-1">Verifica in due passaggi</h2>
                {twoFaMethod === 'totp' ? (
                  <>
                    <p className="text-sm text-gray-400">Inserisci il codice a 6 cifre dalla tua</p>
                    <p className="text-sm text-[#F5A623] font-medium">app Authenticator</p>
                    <p className="text-[11px] text-gray-500 mt-1">Puoi usare anche un codice di backup (8 caratteri)</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-400">Abbiamo inviato un codice a 6 cifre a</p>
                    <p className="text-sm text-[#F5A623] font-medium">{email}</p>
                  </>
                )}
              </div>
              
              <div className="space-y-4">
                <input
                  type="text"
                  inputMode={twoFaMethod === 'totp' ? 'text' : 'numeric'}
                  maxLength={twoFaMethod === 'totp' ? 8 : 6}
                  value={twoFaCode}
                  onChange={(e) => {
                    const raw = e.target.value;
                    // TOTP allows 6 digits OR 8 alphanumeric (backup code)
                    const val = twoFaMethod === 'totp'
                      ? raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
                      : raw.replace(/\D/g, '');
                    setTwoFaCode(val);
                    setTwoFaError("");
                  }}
                  className="w-full h-14 px-4 bg-[#2D2D2D] border border-[#404040] rounded-xl text-center text-2xl font-mono text-white tracking-[0.5em] focus:border-[#F5A623] focus:outline-none transition-colors"
                  placeholder={twoFaMethod === 'totp' ? "000000 / BACKUP8" : "000000"}
                  autoFocus
                  data-testid="2fa-code-input"
                />
                
                {twoFaError && (
                  <div className="flex items-center gap-2 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{twoFaError}</span>
                  </div>
                )}
                
                <button
                  onClick={handleVerify2FA}
                  disabled={twoFaLoading || (twoFaMethod === 'totp' ? (twoFaCode.length !== 6 && twoFaCode.length !== 8) : twoFaCode.length !== 6)}
                  className="w-full h-12 bg-gradient-to-r from-[#F5A623] to-[#FF6B6B] text-[#1A1A1A] font-bold rounded-xl disabled:opacity-50 flex items-center justify-center gap-2 transition-opacity"
                  data-testid="2fa-verify-btn"
                >
                  {twoFaLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verifica'}
                </button>
                
                <button
                  onClick={() => { setShow2FA(false); setTwoFaCode(""); setTwoFaError(""); setTwoFaToken(""); }}
                  className="w-full py-2 text-gray-500 text-sm hover:text-gray-300 transition-colors"
                  data-testid="2fa-back-btn"
                >
                  Torna al login
                </button>
              </div>
              
              <p className="text-xs text-gray-600 text-center mt-4">
                Il codice scade tra 5 minuti
              </p>
            </div>
          </div>
        )}
        
        {/* Main Login/Register Form */}
        {!show2FA && (
        <>
        <div className="text-center mb-10">
          <img src={BRAND.logo} alt={BRAND.name} className="w-24 h-24 mx-auto mb-4 rounded-2xl object-contain shadow-[0_0_30px_rgba(245,166,35,0.2)]" />
          <h1 className="text-5xl font-black uppercase tracking-tighter text-white mb-1" style={{fontFamily: 'Barlow Condensed'}}>{BRAND.name}</h1>
        </div>
        
        <div className="glass rounded-3xl p-6">
          <div className="flex mb-6 bg-black/30 rounded-2xl p-1">
            <button onClick={() => setIsLogin(true)} className={`flex-1 py-3 text-sm font-bold rounded-xl transition-all ${isLogin ? "bg-gradient-to-r from-[#F5A623] to-[#FF6B6B] text-[#1A1A1A]" : "text-gray-500"}`} data-testid="login-tab">Accedi</button>
            <button onClick={() => setIsLogin(false)} className={`flex-1 py-3 text-sm font-bold rounded-xl transition-all ${!isLogin ? "bg-gradient-to-r from-[#F5A623] to-[#FF6B6B] text-[#1A1A1A]" : "text-gray-500"}`} data-testid="register-tab">Registrati</button>
          </div>
          
          {error && <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}
          {success && <div className="flex items-center gap-2 p-3 mb-4 bg-green-500/10 border border-green-500/20 rounded-xl text-green-400 text-sm"><Check className="w-4 h-4 flex-shrink-0" />{success}</div>}


          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(e); }} className="space-y-4">
            {!isLogin && <input type="text" placeholder="Nome" value={name} onChange={(e) => setName(e.target.value)} className="w-full h-14 px-5 bg-black/30 border border-gray-700 rounded-xl text-white placeholder:text-gray-600 focus:border-[#F5A623]" data-testid="name-input" />}
            <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-14 px-5 bg-black/30 border border-gray-700 rounded-xl text-white placeholder:text-gray-600 focus:border-[#F5A623]" data-testid="email-input" />
            <div className="relative">
              <input type={showPassword ? "text" : "password"} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full h-14 px-5 pr-12 bg-black/30 border border-gray-700 rounded-xl text-white placeholder:text-gray-600 focus:border-[#F5A623]" data-testid="password-input" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors p-3 min-w-[44px] min-h-[44px] flex items-center justify-center" data-testid="toggle-password-visibility">{showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
            </div>
            {!isLogin && <PasswordStrengthIndicator password={password} />}

            {isLogin && (
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${rememberMe ? 'bg-[#F5A623] border-[#F5A623]' : 'border-gray-600 group-hover:border-gray-500'}`}>{rememberMe && <Check className="w-3 h-3 text-[#1A1A1A]" />}</div>
                  <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="hidden" data-testid="remember-me-checkbox" />
                  <span className="text-sm text-gray-400">Ricordami</span>
                </label>
                <div className="flex flex-col items-end gap-1">
                  <button type="button" onClick={() => setShowForgotPassword(true)} className="text-[#F5A623] text-sm hover:underline" data-testid="forgot-password-link">Password dimenticata?</button>
                  <button type="button" onClick={() => setShowResendVerification(true)} className="text-gray-400 text-xs hover:text-[#F5A623] hover:underline" data-testid="resend-verification-link">Non hai ricevuto l'email di conferma?</button>
                </div>
              </div>
            )}

            {!isLogin && (
              <div className="space-y-3 pt-2">
                {/* iter254e — Advanced options collapsed. Birth date, phone,
                    referral code and marketing consent are all optional and
                    can be filled post-signup via the profile-completion
                    nudge in the Dashboard. */}
                <SignupTermsCheckbox checked={acceptsTerms} onChange={setAcceptsTerms} testId="signup-terms-checkbox" />
                <label className="flex items-start gap-3 cursor-pointer group" data-testid="meta-tracking-consent-label">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${metaTrackingConsent ? 'bg-[#F5A623] border-[#F5A623]' : 'border-gray-600 group-hover:border-gray-500'}`}>{metaTrackingConsent && <Check className="w-3 h-3 text-[#1A1A1A]" />}</div>
                  <input type="checkbox" checked={metaTrackingConsent} onChange={(e) => { setMetaTrackingConsent(e.target.checked); if (e.target.checked) ensurePixelInit(); }} className="hidden" data-testid="meta-tracking-checkbox" />
                  <span className="text-sm text-gray-400">Aiutaci a far conoscere RVincente: consenti a Meta di misurare le nostre pubblicità. <a href="/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#F5A623] underline">Info</a></span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer group" data-testid="email-marketing-consent-label">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${marketingConsent ? 'bg-[#F5A623] border-[#F5A623]' : 'border-gray-600 group-hover:border-gray-500'}`}>{marketingConsent && <Check className="w-3 h-3 text-[#1A1A1A]" />}</div>
                  <input type="checkbox" checked={marketingConsent} onChange={(e) => setMarketingConsent(e.target.checked)} className="hidden" data-testid="email-marketing-checkbox" />
                  <span className="text-sm text-gray-400">Voglio ricevere notifiche ed email con le analisi e le novità di RVincente. <a href="/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#F5A623] underline">Info</a></span>
                </label>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Codice invito o influencer <span className="text-gray-600">(facoltativo)</span></label>
                  <input
                    type="text"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                    placeholder="Es. KR45 o MARIO"
                    maxLength={8}
                    autoCapitalize="characters"
                    className="w-full h-12 px-4 bg-black/30 border border-gray-700 rounded-xl text-white uppercase tracking-widest font-mono text-center placeholder:text-gray-600 placeholder:normal-case placeholder:tracking-normal placeholder:font-sans focus:border-[#F5A623]"
                    data-testid="referral-code-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                  className="w-full text-xs text-gray-400 hover:text-[#F5A623] py-2 flex items-center justify-center gap-1"
                  data-testid="toggle-advanced-options"
                >
                  {showAdvancedOptions ? '− Nascondi' : '+ Aggiungi un numero di telefono'}
                </button>

                {showAdvancedOptions && (
                  <>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Telefono <span className="text-gray-600">(opzionale)</span></label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+39 333 1234567"
                    autoComplete="tel"
                    inputMode="tel"
                    className="w-full h-12 px-4 bg-black/30 border border-gray-700 rounded-xl text-white placeholder:text-gray-600 focus:border-[#F5A623]"
                    data-testid="phone-input"
                  />
                </div>
                  </>
                )}
              </div>
            )}

            {/* iter279 — Social sotto i campi (e la spunta Meta), sopra il pulsante principale, in entrambe le tab. */}
            <div data-testid="social-login-block">
          {googlePolling ? (
            <div className="w-full h-14 bg-white/10 border border-white/20 text-white rounded-xl flex items-center justify-center gap-3 font-bold mb-3" data-testid="google-polling-indicator">
              <Loader2 className="w-5 h-5 animate-spin" />
              In attesa di login Google...
              <button type="button" onClick={() => { setGooglePolling(false); setLoginIntentId(null); }} className="ml-2 text-xs text-gray-400 underline">Annulla</button>
            </div>
          ) : (
            <button type="button" onClick={handleGoogleLogin} className="w-full h-14 bg-white text-gray-900 rounded-xl flex items-center justify-center gap-3 font-bold hover:bg-gray-100 transition-colors shadow-lg" data-testid="google-login-btn-top">
              <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              {isLogin ? "Accedi" : "Registrati"} con Google
            </button>
          )}

          {isIOS() && (
            <button type="button" onClick={handleAppleSignIn} disabled={loading} className="w-full h-14 bg-black text-white rounded-xl flex items-center justify-center gap-3 font-bold border border-gray-700 hover:bg-gray-900 transition-colors mt-3 shadow-lg" data-testid="apple-login-btn-top">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
              {isLogin ? "Accedi" : "Registrati"} con Apple
            </button>
          )}

                <div className="relative my-5"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-700" /></div><div className="relative flex justify-center text-xs"><span className="bg-[#242424] px-3 text-gray-600">oppure</span></div></div>
            </div>
            {!isLogin && <TurnstileWidget onToken={setCaptchaFor('register')} resetKey={captchaReset.register} testId="register-turnstile" />}
            <button type="submit" disabled={loading} className="w-full h-14 btn-primary rounded-xl flex items-center justify-center gap-2 text-sm touch-manipulation" data-testid="submit-btn" onClick={(e) => { if (!loading) { const form = e.target.closest('form'); if (form) form.requestSubmit(); } }}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (isLogin ? "Accedi" : "Registrati")}
            </button>

          </form>
          
          <div className="relative my-6" />

          {/* iter254c — Social login buttons moved to the top of the card. */}

        </div>
        
        <LegalFooter />
      </>
      )}
      </div>
      
      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="forgot-password-modal">
          <div className="bg-[#242424] rounded-3xl p-6 w-full max-w-sm relative">
            <button onClick={() => { setShowForgotPassword(false); setForgotEmail(""); setForgotSuccess(""); setForgotError(""); }} className="absolute top-4 right-4 text-gray-500 hover:text-white"><X className="w-6 h-6" /></button>
            <h2 className="text-xl font-bold text-white mb-2">Password Dimenticata?</h2>
            <p className="text-gray-400 text-sm mb-6">Inserisci la tua email e ti invieremo una password temporanea.</p>
            {forgotError && <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" />{forgotError}</div>}
            {forgotSuccess && <div className="flex items-center gap-2 p-3 mb-4 bg-green-500/10 border border-green-500/20 rounded-xl text-green-400 text-sm"><Check className="w-4 h-4 flex-shrink-0" />{forgotSuccess}</div>}
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <input type="email" placeholder="La tua email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required className="w-full h-14 px-5 bg-black/30 border border-gray-700 rounded-xl text-white placeholder:text-gray-600 focus:border-[#F5A623]" data-testid="forgot-email-input" />
              <TurnstileWidget onToken={setCaptchaFor('forgot')} resetKey={captchaReset.forgot} testId="forgot-turnstile" />
              <button type="submit" disabled={forgotLoading} className="w-full h-14 btn-primary rounded-xl flex items-center justify-center gap-2" data-testid="forgot-submit-btn">{forgotLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Invia Password Temporanea"}</button>
            </form>
          </div>
        </div>
      )}
      
      {/* Resend Verification Email Modal */}
      {showResendVerification && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="resend-verification-modal">
          <div className="bg-[#242424] rounded-3xl p-6 w-full max-w-sm relative">
            <button onClick={() => { setShowResendVerification(false); setResendEmail(""); setResendSuccess(""); setResendError(""); }} className="absolute top-4 right-4 text-gray-500 hover:text-white"><X className="w-6 h-6" /></button>
            <div className="text-center mb-4">
              <div className="w-16 h-16 rounded-full bg-[#F5A623]/20 flex items-center justify-center mx-auto mb-4"><Mail className="w-8 h-8 text-[#F5A623]" /></div>
              <h2 className="text-xl font-bold text-white mb-2">Rinvia Email di Conferma</h2>
              <p className="text-gray-400 text-sm">Inserisci la tua email e ti invieremo nuovamente il link di conferma.</p>
            </div>
            {resendError && <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" />{resendError}</div>}
            {resendSuccess && <div className="flex items-center gap-2 p-3 mb-4 bg-green-500/10 border border-green-500/20 rounded-xl text-green-400 text-sm"><Check className="w-4 h-4 flex-shrink-0" />{resendSuccess}</div>}
            <form onSubmit={handleResendVerification} className="space-y-4">
              <input type="email" placeholder="La tua email" value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} required className="w-full h-14 px-5 bg-black/30 border border-gray-700 rounded-xl text-white placeholder:text-gray-600 focus:border-[#F5A623]" data-testid="resend-email-input" />
              <TurnstileWidget onToken={setCaptchaFor('resend')} resetKey={captchaReset.resend} testId="resend-turnstile" />
              <button type="submit" disabled={resendLoading} className="w-full h-14 btn-primary rounded-xl flex items-center justify-center gap-2" data-testid="resend-submit-btn">{resendLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Invia Email di Conferma"}</button>
              <div className="relative my-4"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-700"></div></div><div className="relative flex justify-center text-sm"><span className="px-2 bg-[#242424] text-gray-500">oppure</span></div></div>
              <button type="button" onClick={() => { setShowResendVerification(false); navigate('/chat'); }} className="w-full h-14 bg-green-600/20 text-green-400 border border-green-500/30 rounded-xl flex items-center justify-center gap-2 hover:bg-green-600/30 transition-all" data-testid="verify-with-support-btn"><MessageCircle className="w-5 h-5" />Verifica con Supporto</button>
              <p className="text-xs text-gray-500 text-center">Chatta con il supporto per verificare manualmente il tuo account</p>
            </form>
          </div>
        </div>
      )}
      
      {/* Force Change Password Modal */}
      {showForceChangePassword && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="force-change-password-modal">
          <div className="bg-[#242424] rounded-3xl p-6 w-full max-w-sm">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-[#F5A623]/20 flex items-center justify-center mx-auto mb-4"><Shield className="w-8 h-8 text-[#F5A623]" /></div>
              <h2 className="text-xl font-bold text-white mb-2">Cambia Password</h2>
              <p className="text-gray-400 text-sm">Hai effettuato l'accesso con una password temporanea. Imposta una nuova password per continuare.</p>
            </div>
            {changeError && <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" />{changeError}</div>}
            <form onSubmit={handleForceChangePassword} className="space-y-4">
              <div className="relative">
                <input type={showNewPassword ? "text" : "password"} placeholder="Nuova Password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} className="w-full h-14 px-5 pr-12 bg-black/30 border border-gray-700 rounded-xl text-white placeholder:text-gray-600 focus:border-[#F5A623]" data-testid="new-password-input" />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors p-3 min-w-[44px] min-h-[44px] flex items-center justify-center" data-testid="toggle-new-password-visibility">{showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
              </div>
              <PasswordStrengthIndicator password={newPassword} />
              <div className="relative">
                <input type={showConfirmNewPassword ? "text" : "password"} placeholder="Conferma Nuova Password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} required minLength={6} className="w-full h-14 px-5 pr-12 bg-black/30 border border-gray-700 rounded-xl text-white placeholder:text-gray-600 focus:border-[#F5A623]" data-testid="confirm-new-password-input" />
                <button type="button" onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)} className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors p-3 min-w-[44px] min-h-[44px] flex items-center justify-center" data-testid="toggle-confirm-new-password-visibility">{showConfirmNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
              </div>
              <button type="submit" disabled={changeLoading} className="w-full h-14 btn-primary rounded-xl flex items-center justify-center gap-2" data-testid="change-password-submit-btn">{changeLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Imposta Nuova Password"}</button>
            </form>
          </div>
        </div>
      )}
      
      {/* Registration Success Modal */}
      {showRegistrationSuccess && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="registration-success-modal">
          <div className="bg-[#242424] rounded-3xl p-8 w-full max-w-sm text-center relative animate-[fadeIn_0.3s_ease-out]">
            <div className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6 animate-[scaleIn_0.5s_ease-out]"><div className="w-16 h-16 rounded-full bg-green-500/30 flex items-center justify-center"><Check className="w-10 h-10 text-green-400" /></div></div>
            <h2 className="text-2xl font-black text-white mb-3" style={{fontFamily: 'Barlow Condensed'}}>REGISTRAZIONE COMPLETATA!</h2>
            <p className="text-gray-300 mb-2">Abbiamo inviato un'email di conferma a:</p>
            <p className="text-[#F5A623] font-bold mb-6 break-all">{registeredEmail}</p>
            <div className="bg-[#1A1A1A] rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3 text-left">
                <Mail className="w-8 h-8 text-[#F5A623] flex-shrink-0" />
                <div><p className="text-sm text-white font-bold">Controlla la tua email</p><p className="text-xs text-gray-400">Clicca sul link di conferma per attivare il tuo account</p></div>
              </div>
            </div>
            <button onClick={() => { setShowRegistrationSuccess(false); setIsLogin(true); }} className="w-full h-14 btn-primary rounded-xl font-bold" data-testid="registration-success-close-btn">Ho Capito</button>
            <p className="text-xs text-gray-500 mt-4">Non hai ricevuto l'email? Controlla la cartella spam o{' '}<button onClick={() => { setShowRegistrationSuccess(false); setShowResendVerification(true); setResendEmail(registeredEmail); }} className="text-[#F5A623] hover:underline">richiedi un nuovo invio</button></p>
          </div>
        </div>
      )}

      {/* Apple Sign In Consent Modal */}
      {showAppleConsent && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="apple-consent-modal">
          <div className="bg-[#242424] rounded-3xl p-6 w-full max-w-sm relative">
            <button onClick={() => { setShowAppleConsent(false); setAppleAuthData(null); }} className="absolute top-4 right-4 text-gray-500 hover:text-white"><X className="w-6 h-6" /></button>
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Completa la Registrazione</h2>
              <p className="text-gray-400 text-sm">Per completare la registrazione con Apple, conferma:</p>
            </div>
            {error && <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}
            <div className="space-y-4 mb-6">
              <SignupTermsCheckbox checked={appleConsentTerms} onChange={setAppleConsentTerms} testId="apple-consent-terms" />
              <label className="flex items-start gap-3 cursor-pointer" data-testid="apple-consent-meta-label">
                <input type="checkbox" checked={appleConsentMeta} onChange={(e) => { setAppleConsentMeta(e.target.checked); if (e.target.checked) ensurePixelInit(); }} className="mt-1 accent-[#F5A623]" data-testid="apple-consent-meta" />
                <span className="text-sm text-gray-300">Aiutaci a far conoscere RVincente: consenti a Meta di misurare le nostre pubblicità. <a href="/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#F5A623] underline">Info</a></span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="apple-consent-marketing-label">
                <input type="checkbox" checked={appleConsentMarketing} onChange={(e) => setAppleConsentMarketing(e.target.checked)} className="mt-1 accent-[#F5A623]" data-testid="apple-consent-marketing" />
                <span className="text-sm text-gray-300">Voglio ricevere notifiche ed email con le analisi e le novità di RVincente. <a href="/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#F5A623] underline">Info</a></span>
              </label>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Codice invito o influencer <span className="text-gray-600">(facoltativo)</span></label>
                <input
                  type="text"
                  value={appleConsentReferralCode}
                  onChange={(e) => setAppleConsentReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                  placeholder="Es. KR45 o MARIO"
                  maxLength={8}
                  autoCapitalize="characters"
                  className="w-full h-11 px-4 bg-black/30 border border-gray-700 rounded-xl text-white uppercase tracking-widest font-mono text-center placeholder:text-gray-600 placeholder:normal-case placeholder:tracking-normal placeholder:font-sans focus:border-[#F5A623]"
                  data-testid="apple-consent-referral-code"
                />
                <p className="text-[10px] text-gray-500 mt-1">Se un amico ti ha invitato, inserisci qui il suo codice</p>
              </div>
            </div>
            <button onClick={() => handleAppleConsentSubmit()} disabled={loading} className="w-full h-14 btn-primary rounded-xl font-bold flex items-center justify-center gap-2" data-testid="apple-consent-submit">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Completa Registrazione"}
            </button>
          </div>
        </div>
      )}

      {/* Google GIS Consent Modal (new users) */}
      {showGoogleConsent && googlePendingData && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="google-consent-modal">
          <div className="bg-[#242424] rounded-3xl p-6 w-full max-w-sm relative">
            <button onClick={() => { setShowGoogleConsent(false); setGooglePendingData(null); setGoogleConsent18(false); setGoogleConsentNotif(false); setGoogleConsentMarketing(false); }} className="absolute top-4 right-4 text-gray-500 hover:text-white"><X className="w-6 h-6" /></button>
            <div className="text-center mb-6">
              {googlePendingData.picture && <img src={googlePendingData.picture} alt="" className="w-16 h-16 rounded-full mx-auto mb-3" />}
              <h2 className="text-xl font-bold text-white mb-2">Completa la Registrazione</h2>
              <p className="text-gray-400 text-sm">Ciao {googlePendingData.name}! Per completare la registrazione con Google, conferma:</p>
            </div>
            {error && <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}
            <div className="space-y-4 mb-6">
              <SignupTermsCheckbox checked={googleConsentTerms} onChange={setGoogleConsentTerms} testId="google-consent-terms" />
              <label className="flex items-start gap-3 cursor-pointer group" data-testid="google-consent-meta-label">
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${googleConsentMeta ? 'bg-[#F5A623] border-[#F5A623]' : 'border-gray-600 group-hover:border-gray-500'}`}>{googleConsentMeta && <Check className="w-3 h-3 text-[#1A1A1A]" />}</div>
                <input type="checkbox" checked={googleConsentMeta} onChange={(e) => { setGoogleConsentMeta(e.target.checked); if (e.target.checked) ensurePixelInit(); }} className="hidden" data-testid="google-consent-meta" />
                <span className="text-sm text-gray-400">Aiutaci a far conoscere RVincente: consenti a Meta di misurare le nostre pubblicità. <a href="/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#F5A623] underline">Info</a></span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer group" data-testid="google-consent-marketing-label">
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${googleConsentMarketing ? 'bg-[#F5A623] border-[#F5A623]' : 'border-gray-600 group-hover:border-gray-500'}`}>{googleConsentMarketing && <Check className="w-3 h-3 text-[#1A1A1A]" />}</div>
                <input type="checkbox" checked={googleConsentMarketing} onChange={(e) => setGoogleConsentMarketing(e.target.checked)} className="hidden" data-testid="google-consent-marketing" />
                <span className="text-sm text-gray-400">Voglio ricevere notifiche ed email con le analisi e le novità di RVincente. <a href="/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#F5A623] underline">Info</a></span>
              </label>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Codice invito o influencer <span className="text-gray-600">(facoltativo)</span></label>
                <input
                  type="text"
                  value={googleConsentReferralCode}
                  onChange={(e) => setGoogleConsentReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                  placeholder="Es. KR45 o MARIO"
                  maxLength={8}
                  autoCapitalize="characters"
                  className="w-full h-11 px-4 bg-black/30 border border-gray-700 rounded-xl text-white uppercase tracking-widest font-mono text-center placeholder:text-gray-600 placeholder:normal-case placeholder:tracking-normal placeholder:font-sans focus:border-[#F5A623]"
                  data-testid="google-consent-referral-code"
                />
                <p className="text-[10px] text-gray-500 mt-1">Se un amico ti ha invitato, inserisci qui il suo codice</p>
              </div>
            </div>
            <button onClick={() => handleGoogleConsentSubmit()} disabled={loading} className="w-full h-14 btn-primary rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="google-consent-submit">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Completa Registrazione"}
            </button>
          </div>
        </div>
      )}
      <MetaConsentReminder open={!!metaReminder} onAccept={() => metaReminder?.accept()} onDecline={() => metaReminder?.decline()} onClose={closeMetaReminder} />
      <EmailConsentReminder open={!!emailReminder} onAccept={() => emailReminder?.accept()} onDecline={() => emailReminder?.decline()} onClose={closeEmailReminder} />
    </div>
  );
};

export default LoginPage;
